import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Typography,
  message,
  Row,
  Col,
  Card,
  Divider,
  Descriptions,
  Empty,
  Segmented,
  Alert,
  Upload,
  Switch
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  InboxOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { utils, writeFile } from 'xlsx';
import { ordenesCompraService, proveedoresService, productosService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import OrdenCompraPrint from '../components/print/OrdenCompraPrint';
import OrdenCompraPrintProveedor from '../components/print/OrdenCompraPrintProveedor';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const METODOS_ENVIO = ['Paquetería', 'Entrega directa', 'Recolección en sucursal', 'Mensajería', 'Otro'];

const DIRECCION_ENTREGA_DEFAULT =
  'Calle Sur 2, MZ 4, LT 16, # 37. Ciudad Industrial. Tizayuca Hgo. HIDALGO MÉX.. C.P.:43805. R.F.C.:RAD081111EK7';

const STATUS_CONFIG = {
  creada:     { color: 'processing', label: 'Pend. Autorización' },
  autorizada: { color: 'blue',       label: 'Autorizada' },
  rechazada:  { color: 'error',      label: 'Rechazada' },
  entregada:  { color: 'success',    label: 'Entregada' },
  cancelada:  { color: 'default',    label: 'Cancelada' }
};

const formatCurrency = (amount) => {
  if (amount == null || amount === '') return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

const API_STORAGE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api\/?$/, '');
const storageUrl = (path) => `${API_STORAGE_BASE}/storage/${path}`;

const ESTADO_RECEPCION_CONFIG = {
  ok:       { color: 'success', label: 'Bien' },
  dañado:   { color: 'error',   label: 'Dañado' },
  faltante: { color: 'error',   label: 'Faltante' },
  otro:     { color: 'warning', label: 'Otro problema' }
};

const diasAbierta = (orden) => {
  if (!orden.fecha_entrega_real) return null;
  return dayjs().diff(dayjs(orden.fecha_entrega_real), 'day');
};

const ESTADO_RECEPCION_OPTIONS = [
  { label: 'Bien', value: 'ok', icon: <CheckCircleOutlined /> },
  { label: 'Dañado', value: 'dañado' },
  { label: 'Faltante', value: 'faltante' },
  { label: 'Otro', value: 'otro' }
];

const ESTADO_PAGO_CONFIG = {
  no_pagado: { color: 'default', label: 'No pagado' },
  parcial:   { color: 'orange',  label: 'Pago parcial' },
  pagado:    { color: 'success', label: 'Pagado' }
};

const METODOS_PAGO = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'otro', label: 'Otro' }
];

const diasParaVencer = (orden) => {
  if (!orden.fecha_limite_pago || orden.estado_pago === 'pagado') return null;
  return dayjs(orden.fecha_limite_pago).diff(dayjs().startOf('day'), 'day');
};

function OrdenesCompra() {
  const { user } = useAuth();
  const canCreate = ['root', 'admin', 'gerencia'].includes(user?.rol);
  const canAutorizar = ['root', 'gerencia'].includes(user?.rol);
  const canEntregar = ['root', 'admin', 'gerencia'].includes(user?.rol);
  const canDelete = user?.rol === 'root';
  const canPagar = ['root', 'gerencia'].includes(user?.rol);

  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [proveedorFilter, setProveedorFilter] = useState(null);
  const [fechaRange, setFechaRange] = useState(null);
  const [incidenciaFilter, setIncidenciaFilter] = useState('');

  const [proveedores, setProveedores] = useState([]);
  const [productosProveedor, setProductosProveedor] = useState([]);
  const [productosLoading, setProductosLoading] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedProveedorId, setSelectedProveedorId] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [productoToAdd, setProductoToAdd] = useState(null);
  const [editingOrden, setEditingOrden] = useState(null);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const numeroSugeridoRef = useRef('');
  const [form] = Form.useForm();

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrden, setDetailOrden] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [entregaModalOpen, setEntregaModalOpen] = useState(false);
  const [entregaOrden, setEntregaOrden] = useState(null);
  const [entregaChecklist, setEntregaChecklist] = useState([]);
  const [entregaNotas, setEntregaNotas] = useState('');
  const [entregaSaving, setEntregaSaving] = useState(false);

  const [resolverForm, setResolverForm] = useState({});
  const [resolvingDetalleId, setResolvingDetalleId] = useState(null);

  const [pagoModalOpen, setPagoModalOpen] = useState(false);
  const [pagoOrden, setPagoOrden] = useState(null);
  const [pagoForm, setPagoForm] = useState({});
  const [pagoSaving, setPagoSaving] = useState(false);
  const [eliminandoPagoId, setEliminandoPagoId] = useState(null);

  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);

  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Orden de Compra ${printData?.numero_orden || ''}`,
    pageStyle: `@page { size: A4; margin: 1.2cm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
    onAfterPrint: () => setPrintData(null)
  });

  useEffect(() => {
    if (printData === null) return;
    triggerPrint();
  }, [printData]); // eslint-disable-line

  const [printProveedorData, setPrintProveedorData] = useState(null);
  const printProveedorRef = useRef(null);

  const triggerPrintProveedor = useReactToPrint({
    content: () => printProveedorRef.current,
    documentTitle: `Orden de Compra ${printProveedorData?.numero_orden || ''} - Proveedor`,
    pageStyle: `@page { size: A4; margin: 1.2cm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
    onAfterPrint: () => setPrintProveedorData(null)
  });

  useEffect(() => {
    if (printProveedorData === null) return;
    triggerPrintProveedor();
  }, [printProveedorData]); // eslint-disable-line

  // ── Fetch listado ──────────────────────────────────────────────────────
  const fetchOrdenes = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const s = 'search' in overrides ? overrides.search : search;
      const st = 'status' in overrides ? overrides.status : statusFilter;
      const prov = 'proveedor_id' in overrides ? overrides.proveedor_id : proveedorFilter;
      const range = 'fechaRange' in overrides ? overrides.fechaRange : fechaRange;

      const params = {};
      if (s) params.search = s;
      if (st) params.status = st;
      if (prov) params.proveedor_id = prov;
      if (range && range[0] && range[1]) {
        params.fecha_desde = range[0].format('YYYY-MM-DD');
        params.fecha_hasta = range[1].format('YYYY-MM-DD');
      }

      const res = await ordenesCompraService.getAll(params);
      setOrdenes(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar órdenes de compra');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, proveedorFilter, fechaRange]);

  const fetchProveedores = useCallback(async () => {
    try {
      const res = await proveedoresService.getAll({ activo: 1 });
      setProveedores(res.data.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchOrdenes();
    fetchProveedores();
  }, [fetchOrdenes, fetchProveedores]);

  const handleSearch = (value) => {
    setSearch(value);
    fetchOrdenes({ search: value });
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value || '');
    fetchOrdenes({ status: value || '' });
  };

  const handleProveedorFilter = (value) => {
    setProveedorFilter(value || null);
    fetchOrdenes({ proveedor_id: value || null });
  };

  const handleFechaRangeFilter = (range) => {
    setFechaRange(range);
    fetchOrdenes({ fechaRange: range });
  };

  // ── Modal de creación / edición ─────────────────────────────────────────
  const fetchNumeroSugerido = async (proveedorId) => {
    try {
      const res = await ordenesCompraService.getSiguienteNumero(
        proveedorId ? { proveedor_id: proveedorId } : {}
      );
      const sugerido = res.data.data?.numero_orden || '';
      const actual = form.getFieldValue('numero_orden');
      if (!actual || actual === numeroSugeridoRef.current) {
        form.setFieldsValue({ numero_orden: sugerido });
      }
      numeroSugeridoRef.current = sugerido;
    } catch {
      // sugerencia no crítica: si falla, el usuario puede escribir el número manualmente
    }
  };

  const openCreateModal = () => {
    setEditingOrden(null);
    form.resetFields();
    form.setFieldsValue({ fecha_pedido: dayjs(), direccion_entrega: DIRECCION_ENTREGA_DEFAULT });
    setSelectedProveedorId(null);
    setProductosProveedor([]);
    setLineItems([]);
    setProductoToAdd(null);
    numeroSugeridoRef.current = '';
    setCreateModalOpen(true);
    fetchNumeroSugerido(null);
  };

  const openEditModal = async (record) => {
    setEditingOrden(record);
    form.resetFields();
    setLineItems([]);
    setProductoToAdd(null);
    setCreateModalOpen(true);
    setEditModalLoading(true);
    try {
      const res = await ordenesCompraService.getById(record.id);
      const orden = res.data.data;

      setSelectedProveedorId(orden.proveedor_id);
      form.setFieldsValue({
        numero_orden: orden.numero_orden,
        codigo: orden.codigo,
        fecha_pedido: orden.fecha_pedido ? dayjs(orden.fecha_pedido) : null,
        fecha_entrega_estimada: orden.fecha_entrega_estimada ? dayjs(orden.fecha_entrega_estimada) : null,
        fecha_limite_pago: orden.fecha_limite_pago ? dayjs(orden.fecha_limite_pago) : null,
        metodo_envio: orden.metodo_envio,
        direccion_entrega: orden.direccion_entrega || DIRECCION_ENTREGA_DEFAULT,
        notas: orden.notas
      });

      setProductosLoading(true);
      const resProd = await productosService.getAll({ proveedor_id: orden.proveedor_id, activo: 1 });
      setProductosProveedor(resProd.data.data || []);

      setLineItems(
        (orden.detalles || []).map((d) => ({
          producto_id: d.producto_id,
          clave: d.producto?.clave,
          nombre: d.producto?.nombre,
          unidad_medida: d.producto?.unidad_medida,
          cantidad: parseFloat(d.cantidad),
          costo_unitario: parseFloat(d.costo_unitario),
          iva_porcentaje: parseFloat(d.iva_porcentaje)
        }))
      );
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar la orden de compra');
      setCreateModalOpen(false);
    } finally {
      setEditModalLoading(false);
      setProductosLoading(false);
    }
  };

  const handleProveedorChange = async (proveedorId) => {
    setSelectedProveedorId(proveedorId);
    setLineItems([]);
    setProductoToAdd(null);
    if (!editingOrden) fetchNumeroSugerido(proveedorId);
    if (!proveedorId) {
      setProductosProveedor([]);
      return;
    }
    setProductosLoading(true);
    try {
      const res = await productosService.getAll({ proveedor_id: proveedorId, activo: 1 });
      setProductosProveedor(res.data.data || []);
    } catch {
      message.error('Error al cargar productos del proveedor');
    } finally {
      setProductosLoading(false);
    }
  };

  const handleAddLineItem = () => {
    if (!productoToAdd) return;
    if (lineItems.some((li) => li.producto_id === productoToAdd)) {
      message.warning('El producto ya está agregado a la orden');
      return;
    }
    const producto = productosProveedor.find((p) => p.id === productoToAdd);
    if (!producto) return;

    setLineItems([
      ...lineItems,
      {
        producto_id: producto.id,
        clave: producto.clave,
        nombre: producto.nombre,
        unidad_medida: producto.unidad_medida,
        cantidad: 1,
        costo_unitario: parseFloat(producto.precio_compra) || 0,
        iva_porcentaje: 16
      }
    ]);
    setProductoToAdd(null);
  };

  const updateLineItem = (producto_id, field, value) => {
    setLineItems((items) =>
      items.map((li) => (li.producto_id === producto_id ? { ...li, [field]: value } : li))
    );
  };

  const removeLineItem = (producto_id) => {
    setLineItems((items) => items.filter((li) => li.producto_id !== producto_id));
  };

  const calcularLinea = (li) => {
    const subtotal = (parseFloat(li.cantidad) || 0) * (parseFloat(li.costo_unitario) || 0);
    const ivaMonto = subtotal * ((parseFloat(li.iva_porcentaje) || 0) / 100);
    return { subtotal, ivaMonto, total: subtotal + ivaMonto };
  };

  const totalesOrden = lineItems.reduce(
    (acc, li) => {
      const { subtotal, ivaMonto, total } = calcularLinea(li);
      return {
        subtotal: acc.subtotal + subtotal,
        iva: acc.iva + ivaMonto,
        total: acc.total + total
      };
    },
    { subtotal: 0, iva: 0, total: 0 }
  );

  const handleSaveOrden = async () => {
    try {
      const values = await form.validateFields();
      if (lineItems.length === 0) {
        message.warning('Agrega al menos un producto a la orden');
        return;
      }

      const payload = {
        numero_orden: values.numero_orden,
        codigo: values.codigo,
        proveedor_id: selectedProveedorId,
        fecha_pedido: values.fecha_pedido.format('YYYY-MM-DD'),
        fecha_entrega_estimada: values.fecha_entrega_estimada
          ? values.fecha_entrega_estimada.format('YYYY-MM-DD')
          : null,
        fecha_limite_pago: values.fecha_limite_pago
          ? values.fecha_limite_pago.format('YYYY-MM-DD')
          : null,
        metodo_envio: values.metodo_envio,
        direccion_entrega: values.direccion_entrega,
        notas: values.notas,
        detalles: lineItems.map((li) => ({
          producto_id: li.producto_id,
          cantidad: li.cantidad,
          costo_unitario: li.costo_unitario,
          iva_porcentaje: li.iva_porcentaje
        }))
      };

      setSaving(true);
      if (editingOrden) {
        await ordenesCompraService.update(editingOrden.id, payload);
        message.success('Orden de compra actualizada exitosamente');
      } else {
        await ordenesCompraService.create(payload);
        message.success('Orden de compra creada exitosamente');
      }

      setCreateModalOpen(false);
      setEditingOrden(null);
      fetchOrdenes();
    } catch (error) {
      if (error.errorFields) return;
      message.error(
        error.displayMessage || `Error al ${editingOrden ? 'actualizar' : 'crear'} la orden de compra`
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Detalle ─────────────────────────────────────────────────────────────
  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await ordenesCompraService.getById(record.id);
      setDetailOrden(res.data.data);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar la orden de compra');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    const res = await ordenesCompraService.getById(id);
    setDetailOrden(res.data.data);
  };

  const handleAutorizar = async (orden) => {
    setActionLoading(true);
    try {
      await ordenesCompraService.autorizar(orden.id);
      message.success('Orden de compra autorizada');
      await refreshDetail(orden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al autorizar la orden');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRechazar = async (orden) => {
    setActionLoading(true);
    try {
      await ordenesCompraService.rechazar(orden.id);
      message.success('Orden de compra rechazada');
      await refreshDetail(orden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al rechazar la orden');
    } finally {
      setActionLoading(false);
    }
  };

  const openEntregaModal = (orden) => {
    setEntregaOrden(orden);
    setEntregaChecklist(
      (orden.detalles || []).map((d) => ({
        id: d.id,
        nombre: d.producto?.nombre || '—',
        clave: d.producto?.clave || '',
        unidad_medida: d.producto?.unidad_medida || '',
        cantidad: parseFloat(d.cantidad),
        cantidad_recibida: parseFloat(d.cantidad),
        estado_recepcion: 'ok',
        nota_recepcion: '',
        evidencia_fotos: []
      }))
    );
    setEntregaNotas('');
    setEntregaModalOpen(true);
  };

  const updateChecklistItem = (id, patch) => {
    setEntregaChecklist((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const handleUploadEvidencia = async (detalleId, { file, onSuccess, onError }) => {
    try {
      const formData = new FormData();
      formData.append('foto', file);
      const res = await ordenesCompraService.subirEvidencia(entregaOrden.id, formData);
      const { path, url } = res.data.data;
      setEntregaChecklist((prev) =>
        prev.map((it) =>
          it.id === detalleId
            ? { ...it, evidencia_fotos: [...it.evidencia_fotos, { uid: file.uid, name: file.name, path, url }] }
            : it
        )
      );
      onSuccess({ path, url });
    } catch (error) {
      message.error(error.displayMessage || 'Error al subir la foto');
      onError(error);
    }
  };

  const handleRemoveEvidencia = (detalleId, uid) => {
    setEntregaChecklist((prev) =>
      prev.map((it) =>
        it.id === detalleId
          ? { ...it, evidencia_fotos: it.evidencia_fotos.filter((f) => f.uid !== uid) }
          : it
      )
    );
  };

  const handleSubmitEntrega = async () => {
    const faltaNota = entregaChecklist.find(
      (it) => it.estado_recepcion !== 'ok' && !it.nota_recepcion?.trim()
    );
    if (faltaNota) {
      message.error(`Describe el problema reportado para "${faltaNota.nombre}"`);
      return;
    }

    setEntregaSaving(true);
    try {
      const payload = {
        detalles: entregaChecklist.map((it) => ({
          id: it.id,
          cantidad_recibida: it.cantidad_recibida,
          estado_recepcion: it.estado_recepcion,
          nota_recepcion: it.nota_recepcion || null,
          evidencia_fotos: it.evidencia_fotos.map((f) => f.path)
        })),
        notas_entrega: entregaNotas || null
      };
      const res = await ordenesCompraService.entregar(entregaOrden.id, payload);
      message.success(res.data.message || 'Entrega confirmada. Inventario actualizado.');
      setEntregaModalOpen(false);
      await refreshDetail(entregaOrden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al confirmar la entrega');
    } finally {
      setEntregaSaving(false);
    }
  };

  const getResolverForm = (detalle) => {
    const restante = Math.max(0, parseFloat(detalle.cantidad) - parseFloat(detalle.cantidad_recibida || 0));
    return {
      cantidad_repuesta: restante,
      nota_resolucion: '',
      cerrar_sin_reponer: false,
      ...(resolverForm[detalle.id] || {})
    };
  };

  const updateResolverForm = (detalleId, patch) => {
    setResolverForm((prev) => ({
      ...prev,
      [detalleId]: { ...(prev[detalleId] || {}), ...patch }
    }));
  };

  const handleResolverIncidencia = async (orden, detalle) => {
    const form = getResolverForm(detalle);
    if (!form.nota_resolucion?.trim()) {
      message.error('Describe cómo se resolvió la incidencia');
      return;
    }
    setResolvingDetalleId(detalle.id);
    try {
      await ordenesCompraService.resolverIncidencia(orden.id, detalle.id, {
        cantidad_repuesta: form.cantidad_repuesta || 0,
        nota_resolucion: form.nota_resolucion,
        cerrar_sin_reponer: !!form.cerrar_sin_reponer
      });
      message.success('Incidencia actualizada');
      setResolverForm((prev) => {
        const next = { ...prev };
        delete next[detalle.id];
        return next;
      });
      await refreshDetail(orden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al resolver la incidencia');
    } finally {
      setResolvingDetalleId(null);
    }
  };

  const openPagoModal = (orden) => {
    setPagoOrden(orden);
    const restante = Math.max(0, parseFloat(orden.total) - parseFloat(orden.monto_pagado || 0));
    setPagoForm({
      monto: restante,
      metodo_pago: 'transferencia',
      fecha_pago: dayjs(),
      referencia: '',
      nota: '',
      comprobante: null
    });
    setPagoModalOpen(true);
  };

  const updatePagoForm = (patch) => setPagoForm((prev) => ({ ...prev, ...patch }));

  const handleUploadComprobante = async ({ file, onSuccess, onError }) => {
    try {
      const formData = new FormData();
      formData.append('comprobante', file);
      const res = await ordenesCompraService.subirComprobante(pagoOrden.id, formData);
      const { path, url } = res.data.data;
      updatePagoForm({ comprobante: { uid: file.uid, name: file.name, path, url } });
      onSuccess({ path, url });
    } catch (error) {
      message.error(error.displayMessage || 'Error al subir el comprobante');
      onError(error);
    }
  };

  const handleSubmitPago = async () => {
    if (!pagoForm.monto || pagoForm.monto <= 0) {
      message.error('El monto del pago debe ser mayor a 0');
      return;
    }
    if (!pagoForm.fecha_pago) {
      message.error('La fecha de pago es requerida');
      return;
    }

    setPagoSaving(true);
    try {
      await ordenesCompraService.registrarPago(pagoOrden.id, {
        monto: pagoForm.monto,
        metodo_pago: pagoForm.metodo_pago,
        fecha_pago: pagoForm.fecha_pago.format('YYYY-MM-DD'),
        referencia: pagoForm.referencia || null,
        nota: pagoForm.nota || null,
        comprobante_path: pagoForm.comprobante?.path || null
      });
      message.success('Pago registrado');
      setPagoModalOpen(false);
      await refreshDetail(pagoOrden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al registrar el pago');
    } finally {
      setPagoSaving(false);
    }
  };

  const handleEliminarPago = async (orden, pago) => {
    setEliminandoPagoId(pago.id);
    try {
      await ordenesCompraService.eliminarPago(orden.id, pago.id);
      message.success('Pago eliminado');
      await refreshDetail(orden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar el pago');
    } finally {
      setEliminandoPagoId(null);
    }
  };

  const handleDelete = async (record) => {
    try {
      await ordenesCompraService.delete(record.id);
      message.success(`Orden "${record.numero_orden}" eliminada exitosamente`);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar la orden');
    }
  };

  const handlePrint = async (record) => {
    try {
      const res = await ordenesCompraService.getById(record.id);
      setPrintData(res.data.data);
    } catch {
      message.error('Error al preparar la impresión');
    }
  };

  const handlePrintProveedor = async (record) => {
    try {
      const res = await ordenesCompraService.getById(record.id);
      setPrintProveedorData(res.data.data);
    } catch {
      message.error('Error al preparar la impresión');
    }
  };

  const buildOrdenesSheets = (lista) => {
    const sheetOrdenes = lista.map((o) => ({
      'N° Orden': o.numero_orden,
      'Código': o.codigo || '',
      'Proveedor': o.proveedor?.nombre_comercial || '',
      'Fecha Pedido': o.fecha_pedido ? dayjs(o.fecha_pedido).format('DD/MM/YYYY') : '',
      'Fecha Entrega Estimada': o.fecha_entrega_estimada ? dayjs(o.fecha_entrega_estimada).format('DD/MM/YYYY') : '',
      'Método de Envío': o.metodo_envio || '',
      'Estado': STATUS_CONFIG[o.status]?.label || o.status,
      'Subtotal': parseFloat(o.subtotal) || 0,
      'IVA': parseFloat(o.iva_total) || 0,
      'Total': parseFloat(o.total) || 0,
      'Estado de Pago': ESTADO_PAGO_CONFIG[o.estado_pago || 'no_pagado']?.label || '',
      'Monto Pagado': parseFloat(o.monto_pagado) || 0,
      'Creada por': o.creador?.nombre || '',
      'Autorizada por': o.autorizador?.nombre || '',
      'Entrega confirmada por': o.entregador?.nombre || ''
    }));

    const sheetDetalle = [];
    lista.forEach((o) => {
      (o.detalles || []).forEach((d) => {
        sheetDetalle.push({
          'N° Orden': o.numero_orden,
          'Proveedor': o.proveedor?.nombre_comercial || '',
          'Clave': d.producto?.clave || '',
          'Producto': d.producto?.nombre || '',
          'Cantidad': parseFloat(d.cantidad) || 0,
          'Unidad': d.producto?.unidad_medida || '',
          'Costo Unitario': parseFloat(d.costo_unitario) || 0,
          'IVA %': parseFloat(d.iva_porcentaje) || 0,
          'Subtotal': parseFloat(d.subtotal) || 0,
          'IVA': parseFloat(d.iva_monto) || 0,
          'Total': parseFloat(d.total) || 0
        });
      });
    });

    return { sheetOrdenes, sheetDetalle };
  };

  const handleExportExcel = () => {
    if (ordenes.length === 0) {
      message.warning('No hay órdenes de compra para exportar');
      return;
    }

    const { sheetOrdenes, sheetDetalle } = buildOrdenesSheets(ordenes);

    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(sheetOrdenes), 'Órdenes de Compra');
    utils.book_append_sheet(wb, utils.json_to_sheet(sheetDetalle), 'Detalle Productos');

    writeFile(wb, `Ordenes_Compra_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  };

  const handleExportOrdenExcel = async (record) => {
    try {
      const res = await ordenesCompraService.getById(record.id);
      const orden = res.data.data;
      const proveedor = orden.proveedor || {};
      const detalles = orden.detalles || [];
      const fmtFecha = (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '');
      const fmtFechaHora = (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '');

      // ── Todo en una sola hoja: cabecera + productos + totales (igual que el impreso) ──
      const filas = [
        ['ORDEN DE COMPRA', orden.numero_orden],
        ['Código', orden.codigo || ''],
        ['Estado', STATUS_CONFIG[orden.status]?.label || orden.status],
        [],
        ['PROVEEDOR'],
        ['Nombre Comercial', proveedor.nombre_comercial || ''],
        ['Código Proveedor', proveedor.codigo || ''],
        ['Contacto', proveedor.contacto_nombre || ''],
        ['Teléfono Contacto', proveedor.contacto_telefono || proveedor.telefono || ''],
        ['Email Contacto', proveedor.contacto_email || proveedor.email || ''],
        ['Dirección', proveedor.direccion || ''],
        [],
        ['DATOS DE LA ORDEN'],
        ['Fecha de Pedido', fmtFecha(orden.fecha_pedido)],
        ['Fecha de Entrega Estimada', fmtFecha(orden.fecha_entrega_estimada)],
        ['Fecha de Autorización', fmtFechaHora(orden.fecha_autorizacion)],
        ['Fecha de Entrega Real', fmtFechaHora(orden.fecha_entrega_real)],
        ['Estado de Pago', ESTADO_PAGO_CONFIG[orden.estado_pago || 'no_pagado']?.label || ''],
        ['Monto Pagado', parseFloat(orden.monto_pagado) || 0],
        ['Fecha Límite de Pago', fmtFecha(orden.fecha_limite_pago)],
        ['Método de Envío', orden.metodo_envio || ''],
        ['Creada por', orden.creador?.nombre || ''],
        ['Autorizada por', orden.autorizador?.nombre || ''],
        ['Entrega confirmada por', orden.entregador?.nombre || ''],
        ...(orden.status === 'rechazada' ? [['Motivo de Rechazo', orden.motivo_rechazo || '']] : []),
        ['Notas', orden.notas || ''],
        [],
        ['DETALLE DE PRODUCTOS'],
        ['Clave', 'Producto', 'Cantidad', 'Unidad', 'Costo Unitario', 'IVA %', 'Subtotal', 'IVA', 'Total'],
        ...detalles.map((d) => [
          d.producto?.clave || '',
          d.producto?.nombre || '',
          parseFloat(d.cantidad) || 0,
          d.producto?.unidad_medida || '',
          parseFloat(d.costo_unitario) || 0,
          parseFloat(d.iva_porcentaje) || 0,
          parseFloat(d.subtotal) || 0,
          parseFloat(d.iva_monto) || 0,
          parseFloat(d.total) || 0
        ]),
        [],
        ['', '', '', '', '', '', 'Subtotal', '', parseFloat(orden.subtotal) || 0],
        ['', '', '', '', '', '', 'IVA', '', parseFloat(orden.iva_total) || 0],
        ['', '', '', '', '', '', 'Total', '', parseFloat(orden.total) || 0],
        ...(orden.tiene_incidencia
          ? [
              [],
              ['INCIDENCIAS DE ENTREGA'],
              ['Clave', 'Producto', 'Cantidad Pedida', 'Cantidad Recibida', 'Estado', 'Detalle', 'Resolución'],
              ...detalles
                .filter((d) => d.estado_recepcion && d.estado_recepcion !== 'ok')
                .map((d) => [
                  d.producto?.clave || '',
                  d.producto?.nombre || '',
                  parseFloat(d.cantidad) || 0,
                  d.cantidad_recibida != null ? parseFloat(d.cantidad_recibida) : '',
                  ESTADO_RECEPCION_CONFIG[d.estado_recepcion]?.label || d.estado_recepcion,
                  d.nota_recepcion || '',
                  d.incidencia_resuelta ? `Resuelta: ${d.nota_resolucion || ''}` : 'Pendiente'
                ]),
              ...(orden.notas_entrega ? [[], ['Notas Generales de la Entrega', orden.notas_entrega]] : [])
            ]
          : []),
        ...(orden.pagos?.length > 0
          ? [
              [],
              ['HISTORIAL DE PAGOS'],
              ['Fecha', 'Monto', 'Método', 'Referencia', 'Nota', 'Registrado por'],
              ...orden.pagos.map((p) => [
                fmtFecha(p.fecha_pago),
                parseFloat(p.monto) || 0,
                METODOS_PAGO.find((m) => m.value === p.metodo_pago)?.label || p.metodo_pago,
                p.referencia || '',
                p.nota || '',
                p.usuario?.nombre || ''
              ])
            ]
          : [])
      ];

      const ws = utils.aoa_to_sheet(filas);
      ws['!cols'] = [
        { wch: 22 }, { wch: 32 }, { wch: 12 }, { wch: 10 },
        { wch: 15 }, { wch: 10 }, { wch: 13 }, { wch: 13 }, { wch: 13 }
      ];

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Orden de Compra');

      writeFile(wb, `Orden_Compra_${orden.numero_orden}.xlsx`);
    } catch {
      message.error('Error al generar el Excel de la orden');
    }
  };

  // ── Columnas ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'N° Orden',
      dataIndex: 'numero_orden',
      key: 'numero_orden',
      width: 140,
      render: (val) => <Text code strong>{val}</Text>
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 120,
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'Proveedor',
      key: 'proveedor',
      render: (_, record) => record.proveedor?.nombre_comercial || '—'
    },
    {
      title: 'Fecha Pedido',
      dataIndex: 'fecha_pedido',
      key: 'fecha_pedido',
      width: 120,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '—')
    },
    {
      title: 'Entrega Estimada',
      dataIndex: 'fecha_entrega_estimada',
      key: 'fecha_entrega_estimada',
      width: 130,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '—')
    },
    {
      title: 'Entrega Confirmada',
      dataIndex: 'fecha_entrega_real',
      key: 'fecha_entrega_real',
      width: 150,
      sorter: (a, b) => new Date(a.fecha_entrega_real || 0) - new Date(b.fecha_entrega_real || 0),
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '—')
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Pago',
      dataIndex: 'estado_pago',
      key: 'estado_pago',
      width: 200,
      filters: [
        ...Object.entries(ESTADO_PAGO_CONFIG).map(([value, cfg]) => ({ text: cfg.label, value })),
        { text: 'Vencidos', value: 'vencido' }
      ],
      onFilter: (value, record) => {
        if (value === 'vencido') {
          const d = diasParaVencer(record);
          return d != null && d < 0;
        }
        return (record.estado_pago || 'no_pagado') === value;
      },
      render: (estadoPago, record) => {
        const estado = estadoPago || 'no_pagado';
        const dias = diasParaVencer(record);
        const vencido = dias != null && dias < 0;
        const porVencer = dias != null && dias >= 0 && dias <= 3;
        return (
          <Space direction="vertical" size={0}>
            <Space size={4} wrap>
              <Tag color={ESTADO_PAGO_CONFIG[estado]?.color}>{ESTADO_PAGO_CONFIG[estado]?.label}</Tag>
              {vencido && (
                <Tooltip title={`Venció hace ${Math.abs(dias)} día(s)`}>
                  <Tag color="red" icon={<WarningOutlined />}>Vencido</Tag>
                </Tooltip>
              )}
              {!vencido && porVencer && (
                <Tooltip title="El pago está por vencer">
                  <Tag color="gold" icon={<WarningOutlined />}>Vence en {dias}d</Tag>
                </Tooltip>
              )}
            </Space>
            {estado !== 'no_pagado' && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {formatCurrency(record.monto_pagado)} / {formatCurrency(record.total)}
              </Text>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 190,
      filters: Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ text: cfg.label, value })),
      onFilter: (value, record) => record.status === value,
      render: (status, record) => {
        const tuvoIncidencia = record.detalles?.some((d) => d.estado_recepcion && d.estado_recepcion !== 'ok');
        const dias = record.tiene_incidencia ? diasAbierta(record) : null;
        return (
          <Space size={4} wrap>
            <Tag color={STATUS_CONFIG[status]?.color}>{STATUS_CONFIG[status]?.label || status}</Tag>
            {record.tiene_incidencia && (
              <Tooltip title="Esta entrega tiene productos con incidencias pendientes de resolver">
                <Tag color={dias >= 7 ? 'red' : 'orange'} icon={<WarningOutlined />}>
                  Incidencia{dias != null ? ` (${dias}d)` : ''}
                </Tag>
              </Tooltip>
            )}
            {!record.tiene_incidencia && tuvoIncidencia && (
              <Tooltip title="Esta entrega tuvo incidencias, ya resueltas con el proveedor">
                <Tag color="blue" icon={<CheckCircleOutlined />}>Resuelta</Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Ver detalle">
            <Button type="primary" ghost size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          </Tooltip>
          {canCreate && record.status === 'creada' && (
            <Tooltip title="Editar">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
            </Tooltip>
          )}
          <Tooltip title="Imprimir">
            <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
          </Tooltip>
          <Tooltip title="Imprimir para Proveedor">
            <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrintProveedor(record)}>
              P
            </Button>
          </Tooltip>
          <Tooltip title="Descargar Excel">
            <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExportOrdenExcel(record)} />
          </Tooltip>
          {canDelete && record.status === 'creada' && (
            <Popconfirm
              title="¿Eliminar orden de compra?"
              description={`La orden "${record.numero_orden}" será eliminada permanentemente.`}
              onConfirm={() => handleDelete(record)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Eliminar">
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Hidden print component */}
      <div style={{ display: 'none' }}>
        <OrdenCompraPrint ref={printRef} orden={printData} />
        <OrdenCompraPrintProveedor ref={printProveedorRef} orden={printProveedorData} />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Órdenes de Compra</Title>
          <Text type="secondary">Gestión de órdenes de compra a proveedores</Text>
        </div>
      </div>

      {/* Filters & Actions */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="Buscar por N° de orden o proveedor..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) fetchOrdenes({ search: '' });
              }}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Filtrar por estado"
              allowClear
              style={{ width: '100%' }}
              value={statusFilter || undefined}
              onChange={handleStatusFilter}
            >
              {Object.entries(STATUS_CONFIG).map(([value, cfg]) => (
                <Option key={value} value={value}>{cfg.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Filtrar por proveedor"
              allowClear
              showSearch
              style={{ width: '100%' }}
              value={proveedorFilter || undefined}
              onChange={handleProveedorFilter}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {proveedores.map((p) => (
                <Option key={p.id} value={p.id}>{p.nombre_comercial}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Fecha desde', 'Fecha hasta']}
              value={fechaRange}
              onChange={handleFechaRangeFilter}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filtrar por incidencias"
              allowClear
              style={{ width: '100%' }}
              value={incidenciaFilter || undefined}
              onChange={(v) => setIncidenciaFilter(v || '')}
            >
              <Option value="abierta">
                <WarningOutlined style={{ color: '#fa8c16' }} /> Con incidencias abiertas
              </Option>
              <Option value="resuelta">
                <CheckCircleOutlined style={{ color: '#1677ff' }} /> Incidencias resueltas
              </Option>
            </Select>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { setSearch(''); setStatusFilter(''); setProveedorFilter(null); setFechaRange(null); setIncidenciaFilter(''); fetchOrdenes({ search: '', status: '', proveedor_id: null, fechaRange: null }); }}
            >
              Limpiar
            </Button>
          </Col>
          <Col>
            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
              Descargar Excel
            </Button>
          </Col>
          {canCreate && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Nueva Orden de Compra
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={ordenes.filter((o) => {
            if (!incidenciaFilter) return true;
            const tuvoIncidencia = o.detalles?.some((d) => d.estado_recepcion && d.estado_recepcion !== 'ok');
            if (incidenciaFilter === 'abierta') return o.tiene_incidencia;
            if (incidenciaFilter === 'resuelta') return !o.tiene_incidencia && tuvoIncidencia;
            return true;
          })}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1320 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} órdenes`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron órdenes de compra' }}
        />
      </Card>

      {/* ── Modal: Nueva / Editar Orden de Compra ────────────────────────── */}
      <Modal
        title={editingOrden ? `Editar Orden de Compra ${editingOrden.numero_orden}` : 'Nueva Orden de Compra'}
        open={createModalOpen}
        onOk={handleSaveOrden}
        onCancel={() => { setCreateModalOpen(false); setEditingOrden(null); }}
        okText={editingOrden ? 'Guardar Cambios' : 'Crear Orden'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={900}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          style={{ marginTop: 8 }}
          disabled={editModalLoading}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="numero_orden"
                label="N° de Orden"
                tooltip="Sugerido automáticamente con el consecutivo de la base de datos. Puedes editarlo; si ya existe, se mostrará un error."
                rules={[{ required: true, message: 'El número de orden es requerido' }, { max: 50, message: 'Máximo 50 caracteres' }]}
              >
                <Input placeholder="Ej: 0001-26-PROV" maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="codigo"
                label="Código"
                tooltip="Código o referencia interna de la orden (opcional)"
                rules={[{ max: 50, message: 'Máximo 50 caracteres' }]}
              >
                <Input placeholder="Ej: REQ-2026-045" maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Proveedor" required>
                <Select
                  placeholder="Selecciona un proveedor"
                  showSearch
                  value={selectedProveedorId}
                  onChange={handleProveedorChange}
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {proveedores.map((p) => (
                    <Option key={p.id} value={p.id}>{p.nombre_comercial}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="fecha_pedido"
                label="Fecha de Pedido"
                rules={[{ required: true, message: 'La fecha de pedido es requerida' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="fecha_entrega_estimada" label="Fecha de Entrega Estimada">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="fecha_limite_pago"
                label="Fecha Límite de Pago"
                tooltip="Opcional. Si se define, se mostrará una alerta cuando el pago esté por vencer o vencido."
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="metodo_envio" label="Método de Envío">
                <Select placeholder="Selecciona un método" allowClear>
                  {METODOS_ENVIO.map((m) => (
                    <Option key={m} value={m}>{m}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="notas" label="Notas">
                <TextArea placeholder="Notas adicionales (opcional)" rows={1} maxLength={500} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="direccion_entrega" label="Dirección de Entrega">
                <TextArea rows={2} maxLength={500} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Productos</Divider>

          <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
            <Col flex="auto">
              <Select
                placeholder={selectedProveedorId ? 'Selecciona un producto para agregar' : 'Primero selecciona un proveedor'}
                showSearch
                disabled={!selectedProveedorId}
                loading={productosLoading}
                value={productoToAdd}
                onChange={setProductoToAdd}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={
                  selectedProveedorId ? 'Este proveedor no tiene productos asociados' : null
                }
              >
                {productosProveedor.map((p) => (
                  <Option key={p.id} value={p.id} disabled={lineItems.some((li) => li.producto_id === p.id)}>
                    {p.clave} — {p.nombre}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Button icon={<PlusOutlined />} onClick={handleAddLineItem} disabled={!productoToAdd}>
                Agregar
              </Button>
            </Col>
          </Row>

          {lineItems.length === 0 ? (
            <Empty description="Sin productos agregados" style={{ margin: '20px 0' }} />
          ) : (
            <Table
              dataSource={lineItems}
              rowKey="producto_id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Producto',
                  key: 'producto',
                  render: (_, li) => (
                    <div>
                      <Text strong style={{ fontSize: 12 }}>{li.clave}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>{li.nombre}</Text>
                    </div>
                  )
                },
                {
                  title: 'Cantidad',
                  key: 'cantidad',
                  width: 110,
                  render: (_, li) => (
                    <InputNumber
                      min={0.001}
                      precision={3}
                      style={{ width: '100%' }}
                      value={li.cantidad}
                      onChange={(v) => updateLineItem(li.producto_id, 'cantidad', v || 0)}
                    />
                  )
                },
                {
                  title: 'Costo Unit.',
                  key: 'costo_unitario',
                  width: 120,
                  render: (_, li) => (
                    <InputNumber
                      min={0}
                      precision={2}
                      prefix="$"
                      style={{ width: '100%' }}
                      value={li.costo_unitario}
                      onChange={(v) => updateLineItem(li.producto_id, 'costo_unitario', v || 0)}
                    />
                  )
                },
                {
                  title: 'IVA %',
                  key: 'iva_porcentaje',
                  width: 90,
                  render: (_, li) => (
                    <InputNumber
                      min={0}
                      max={100}
                      precision={2}
                      style={{ width: '100%' }}
                      value={li.iva_porcentaje}
                      onChange={(v) => updateLineItem(li.producto_id, 'iva_porcentaje', v ?? 0)}
                    />
                  )
                },
                {
                  title: 'Total',
                  key: 'total',
                  width: 110,
                  align: 'right',
                  render: (_, li) => {
                    const { total } = calcularLinea(li);
                    return <Text strong>{formatCurrency(total)}</Text>;
                  }
                },
                {
                  title: '',
                  key: 'acciones',
                  width: 40,
                  render: (_, li) => (
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeLineItem(li.producto_id)}
                    />
                  )
                }
              ]}
            />
          )}

          {lineItems.length > 0 && (
            <Row justify="end" style={{ marginTop: 12 }}>
              <Col>
                <Descriptions column={1} size="small" bordered style={{ width: 260 }}>
                  <Descriptions.Item label="Subtotal">{formatCurrency(totalesOrden.subtotal)}</Descriptions.Item>
                  <Descriptions.Item label="IVA">{formatCurrency(totalesOrden.iva)}</Descriptions.Item>
                  <Descriptions.Item label={<Text strong>Total</Text>}>
                    <Text strong>{formatCurrency(totalesOrden.total)}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          )}
        </Form>
      </Modal>

      {/* ── Modal: Detalle de Orden ──────────────────────────────────────── */}
      <Modal
        title={detailOrden ? `Orden de Compra: ${detailOrden.numero_orden}` : 'Orden de Compra'}
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetailOrden(null); }}
        footer={
          <Space>
            <Button onClick={() => { setDetailOpen(false); setDetailOrden(null); }}>Cerrar</Button>
            {detailOrden && canCreate && detailOrden.status === 'creada' && (
              <Button
                icon={<EditOutlined />}
                onClick={() => { setDetailOpen(false); setDetailOrden(null); openEditModal(detailOrden); }}
              >
                Edit
              </Button>
            )}
            {detailOrden && (
              <Button icon={<PrinterOutlined />} onClick={() => handlePrint(detailOrden)}>
                Imp
              </Button>
            )}
            {detailOrden && (
              <Button icon={<PrinterOutlined />} onClick={() => handlePrintProveedor(detailOrden)}>
                Prov
              </Button>
            )}
            {detailOrden && (
              <Button icon={<FileExcelOutlined />} onClick={() => handleExportOrdenExcel(detailOrden)}>
                Excel
              </Button>
            )}
            {detailOrden && canAutorizar && detailOrden.status === 'creada' && (
              <>
                <Popconfirm
                  title="¿Rechazar orden de compra?"
                  onConfirm={() => handleRechazar(detailOrden)}
                  okText="Sí, rechazar"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger icon={<CloseOutlined />} loading={actionLoading}>
                    Rechazar
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="¿Autorizar orden de compra?"
                  description="Se marcará como autorizada para gestionar el pedido con el proveedor."
                  onConfirm={() => handleAutorizar(detailOrden)}
                  okText="Sí, autorizar"
                  cancelText="Cancelar"
                >
                  <Button type="primary" icon={<CheckOutlined />} loading={actionLoading}>
                    Autorizar
                  </Button>
                </Popconfirm>
              </>
            )}
            {detailOrden && canEntregar && detailOrden.status === 'autorizada' && (
              <Button
                type="primary"
                icon={<InboxOutlined />}
                onClick={() => openEntregaModal(detailOrden)}
              >
                C. Entrega
              </Button>
            )}
            {detailOrden && canPagar && !['rechazada', 'cancelada'].includes(detailOrden.status) && detailOrden.estado_pago !== 'pagado' && (
              <Button icon={<CheckCircleOutlined />} onClick={() => openPagoModal(detailOrden)}>
                R. Pago
              </Button>
            )}
          </Space>
        }
        width={800}
        destroyOnClose
      >
        {detailLoading || !detailOrden ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Cargando...</div>
        ) : (
          <div>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Estado" span={2}>
                <Space size={4} wrap>
                  <Tag color={STATUS_CONFIG[detailOrden.status]?.color}>
                    {STATUS_CONFIG[detailOrden.status]?.label || detailOrden.status}
                  </Tag>
                  {detailOrden.tiene_incidencia && (
                    <Tag color={diasAbierta(detailOrden) >= 7 ? 'red' : 'orange'} icon={<WarningOutlined />}>
                      Con incidencias pendientes{diasAbierta(detailOrden) != null ? ` (${diasAbierta(detailOrden)} días)` : ''}
                    </Tag>
                  )}
                  {detailOrden.status === 'entregada' && !detailOrden.tiene_incidencia && (
                    detailOrden.detalles?.some((d) => d.estado_recepcion && d.estado_recepcion !== 'ok') ? (
                      <Tag color="blue" icon={<CheckCircleOutlined />}>Incidencias resueltas</Tag>
                    ) : (
                      <Tag color="success" icon={<CheckCircleOutlined />}>Recibida sin incidencias</Tag>
                    )
                  )}
                </Space>
                {detailOrden.status === 'rechazada' && detailOrden.motivo_rechazo && (
                  <Text type="secondary"> — {detailOrden.motivo_rechazo}</Text>
                )}
              </Descriptions.Item>
              {detailOrden.codigo && (
                <Descriptions.Item label="Código" span={2}>{detailOrden.codigo}</Descriptions.Item>
              )}
              <Descriptions.Item label="Proveedor">{detailOrden.proveedor?.nombre_comercial}</Descriptions.Item>
              <Descriptions.Item label="Contacto">
                {detailOrden.proveedor?.contacto_nombre || '—'} / {detailOrden.proveedor?.contacto_telefono || detailOrden.proveedor?.telefono || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Pedido">
                {dayjs(detailOrden.fecha_pedido).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Entrega Estimada">
                {detailOrden.fecha_entrega_estimada ? dayjs(detailOrden.fecha_entrega_estimada).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              {detailOrden.fecha_entrega_real && (
                <Descriptions.Item label="Entrega Confirmada">
                  {dayjs(detailOrden.fecha_entrega_real).format('DD/MM/YYYY HH:mm')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Estado de Pago" span={2}>
                <Space size={4} wrap>
                  <Tag color={ESTADO_PAGO_CONFIG[detailOrden.estado_pago || 'no_pagado']?.color}>
                    {ESTADO_PAGO_CONFIG[detailOrden.estado_pago || 'no_pagado']?.label}
                  </Tag>
                  {(detailOrden.estado_pago || 'no_pagado') !== 'no_pagado' && (
                    <Text type="secondary">
                      {formatCurrency(detailOrden.monto_pagado)} de {formatCurrency(detailOrden.total)}
                    </Text>
                  )}
                  {detailOrden.fecha_limite_pago && (() => {
                    const dias = diasParaVencer(detailOrden);
                    if (dias == null) return null;
                    if (dias < 0) return <Tag color="red" icon={<WarningOutlined />}>Venció hace {Math.abs(dias)}d</Tag>;
                    if (dias <= 3) return <Tag color="gold" icon={<WarningOutlined />}>Vence en {dias}d</Tag>;
                    return null;
                  })()}
                </Space>
                {detailOrden.fecha_limite_pago && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Fecha límite de pago: {dayjs(detailOrden.fecha_limite_pago).format('DD/MM/YYYY')}
                    </Text>
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Método de Envío">{detailOrden.metodo_envio || '—'}</Descriptions.Item>
              {detailOrden.direccion_entrega && (
                <Descriptions.Item label="Dirección de Entrega" span={2}>{detailOrden.direccion_entrega}</Descriptions.Item>
              )}
              <Descriptions.Item label="Creada por">{detailOrden.creador?.nombre || '—'}</Descriptions.Item>
              {detailOrden.autorizador?.nombre && (
                <Descriptions.Item label="Autorizada por">{detailOrden.autorizador.nombre}</Descriptions.Item>
              )}
              {detailOrden.entregador?.nombre && (
                <Descriptions.Item label="Entrega confirmada por">{detailOrden.entregador.nombre}</Descriptions.Item>
              )}
              {detailOrden.notas && (
                <Descriptions.Item label="Notas" span={2}>{detailOrden.notas}</Descriptions.Item>
              )}
              {detailOrden.notas_entrega && (
                <Descriptions.Item label="Notas de la Entrega" span={2}>{detailOrden.notas_entrega}</Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left" plain style={{ marginTop: 0 }}>
              <FileTextOutlined /> Productos
            </Divider>
            <Table
              dataSource={detailOrden.detalles || []}
              rowKey="id"
              pagination={false}
              size="small"
              expandable={
                detailOrden.status === 'entregada'
                  ? {
                      rowExpandable: (d) => d.estado_recepcion && d.estado_recepcion !== 'ok',
                      expandedRowRender: (d) => {
                        const form = getResolverForm(d);
                        return (
                          <div>
                            <Text type="danger" strong>
                              {ESTADO_RECEPCION_CONFIG[d.estado_recepcion]?.label || d.estado_recepcion}:
                            </Text>{' '}
                            <Text>{d.nota_recepcion || 'Sin detalle'}</Text>
                            {d.evidencia_fotos?.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <Space wrap>
                                  {d.evidencia_fotos.map((path) => (
                                    <a key={path} href={storageUrl(path)} target="_blank" rel="noreferrer">
                                      <img
                                        src={storageUrl(path)}
                                        alt="Evidencia"
                                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }}
                                      />
                                    </a>
                                  ))}
                                </Space>
                              </div>
                            )}

                            <div style={{ marginTop: 8 }}>
                              <Text strong style={{ fontSize: 12 }}>
                                Recibido hasta ahora: {parseFloat(d.cantidad_recibida || 0).toLocaleString('es-MX')} de {parseFloat(d.cantidad).toLocaleString('es-MX')} {d.producto?.unidad_medida || ''}
                              </Text>
                            </div>

                            {d.resoluciones?.length > 0 && (
                              <div style={{ marginTop: 10 }}>
                                <Text strong style={{ fontSize: 12 }}>Historial de reposiciones</Text>
                                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {d.resoluciones.map((r) => (
                                    <Alert
                                      key={r.id}
                                      type={r.marco_resuelto ? 'success' : 'info'}
                                      showIcon
                                      message={
                                        r.cerrada_sin_reponer
                                          ? 'Cerrada sin reponer (nota de crédito / pérdida aceptada)'
                                          : r.marco_resuelto
                                          ? 'Incidencia resuelta'
                                          : `Reposición parcial: +${parseFloat(r.cantidad_repuesta).toLocaleString('es-MX')} ${d.producto?.unidad_medida || ''}`
                                      }
                                      description={
                                        <>
                                          <div>{r.nota}</div>
                                          <Text type="secondary" style={{ fontSize: 12 }}>
                                            {r.usuario?.nombre || 'Usuario'} — {dayjs(r.created_at).format('DD/MM/YYYY HH:mm')}
                                          </Text>
                                        </>
                                      }
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {!d.incidencia_resuelta && canEntregar && (
                              <Card size="small" style={{ marginTop: 10, background: '#fafafa' }}>
                                <Text strong style={{ fontSize: 12 }}>
                                  Registrar reposición del proveedor
                                  {' '}— aún faltan {Math.max(0, parseFloat(d.cantidad) - parseFloat(d.cantidad_recibida || 0)).toLocaleString('es-MX')} {d.producto?.unidad_medida || ''}
                                </Text>
                                <Row gutter={12} style={{ marginTop: 8 }} align="bottom">
                                  <Col xs={24} sm={7}>
                                    <div style={{ marginBottom: 4 }}><Text type="secondary">Cantidad repuesta ahora</Text></div>
                                    <InputNumber
                                      min={0}
                                      precision={3}
                                      style={{ width: '100%' }}
                                      value={form.cantidad_repuesta}
                                      onChange={(v) => updateResolverForm(d.id, { cantidad_repuesta: v ?? 0 })}
                                    />
                                  </Col>
                                  <Col xs={24} sm={12}>
                                    <div style={{ marginBottom: 4 }}><Text type="secondary">Nota</Text></div>
                                    <Input
                                      placeholder={
                                        form.cerrar_sin_reponer
                                          ? 'Ej. Proveedor emitió nota de crédito por las piezas faltantes'
                                          : 'Ej. Proveedor reenvió 2 piezas, aún faltan 3'
                                      }
                                      value={form.nota_resolucion}
                                      onChange={(e) => updateResolverForm(d.id, { nota_resolucion: e.target.value })}
                                    />
                                  </Col>
                                  <Col xs={24} sm={5}>
                                    <Button
                                      type="primary"
                                      danger={form.cerrar_sin_reponer}
                                      block
                                      loading={resolvingDetalleId === d.id}
                                      onClick={() => handleResolverIncidencia(detailOrden, d)}
                                    >
                                      {form.cerrar_sin_reponer ? 'Cerrar incidencia' : 'Registrar'}
                                    </Button>
                                  </Col>
                                </Row>
                                <div style={{ marginTop: 10 }}>
                                  <Space>
                                    <Switch
                                      size="small"
                                      checked={form.cerrar_sin_reponer}
                                      onChange={(checked) => updateResolverForm(d.id, {
                                        cerrar_sin_reponer: checked,
                                        ...(checked ? { cantidad_repuesta: 0 } : {})
                                      })}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      Cerrar sin reponer más producto (nota de crédito o pérdida aceptada)
                                    </Text>
                                  </Space>
                                </div>
                              </Card>
                            )}
                          </div>
                        );
                      }
                    }
                  : undefined
              }
              columns={[
                { title: 'Producto', key: 'nombre', render: (_, d) => d.producto?.nombre || '—' },
                {
                  title: 'Cantidad', key: 'cantidad', width: 110, align: 'right',
                  render: (_, d) => `${parseFloat(d.cantidad).toLocaleString('es-MX')} ${d.producto?.unidad_medida || ''}`
                },
                ...(detailOrden.status === 'entregada'
                  ? [
                      {
                        title: 'Recibido', key: 'recibido', width: 110, align: 'right',
                        render: (_, d) => d.cantidad_recibida != null
                          ? `${parseFloat(d.cantidad_recibida).toLocaleString('es-MX')} ${d.producto?.unidad_medida || ''}`
                          : '—'
                      },
                      {
                        title: 'Estado', key: 'estado_recepcion', width: 150,
                        render: (_, d) => (
                          <Space size={4}>
                            <Tag color={ESTADO_RECEPCION_CONFIG[d.estado_recepcion]?.color || 'default'}>
                              {ESTADO_RECEPCION_CONFIG[d.estado_recepcion]?.label || '—'}
                            </Tag>
                            {d.estado_recepcion !== 'ok' && (
                              d.incidencia_resuelta
                                ? <Tag color="blue" icon={<CheckCircleOutlined />}>Resuelto</Tag>
                                : <Tag color="orange" icon={<WarningOutlined />}>Pendiente</Tag>
                            )}
                          </Space>
                        )
                      }
                    ]
                  : []),
                { title: 'Costo Unit.', key: 'costo', width: 100, align: 'right', render: (_, d) => formatCurrency(d.costo_unitario) },
                { title: 'IVA %', key: 'iva', width: 70, align: 'right', render: (_, d) => `${parseFloat(d.iva_porcentaje)}%` },
                { title: 'Total', key: 'total', width: 110, align: 'right', render: (_, d) => <Text strong>{formatCurrency(d.total)}</Text> }
              ]}
            />

            <Row justify="end" style={{ marginTop: 12 }}>
              <Col>
                <Descriptions column={1} size="small" bordered style={{ width: 260 }}>
                  <Descriptions.Item label="Subtotal">{formatCurrency(detailOrden.subtotal)}</Descriptions.Item>
                  <Descriptions.Item label="IVA">{formatCurrency(detailOrden.iva_total)}</Descriptions.Item>
                  <Descriptions.Item label={<Text strong>Total</Text>}>
                    <Text strong>{formatCurrency(detailOrden.total)}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            {!['rechazada', 'cancelada'].includes(detailOrden.status) && (
              <>
                <Divider orientation="left" plain>
                  <CheckCircleOutlined /> Historial de Pagos
                </Divider>
                {detailOrden.pagos?.length > 0 ? (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {detailOrden.pagos.map((pago) => (
                      <Card key={pago.id} size="small">
                        <Row justify="space-between" align="top">
                          <Col>
                            <Text strong>{formatCurrency(pago.monto)}</Text>{' '}
                            <Tag>{METODOS_PAGO.find((m) => m.value === pago.metodo_pago)?.label || pago.metodo_pago}</Tag>
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(pago.fecha_pago).format('DD/MM/YYYY')}
                                {pago.referencia ? ` · Ref: ${pago.referencia}` : ''}
                                {' · '}{pago.usuario?.nombre || 'Usuario'}
                              </Text>
                            </div>
                            {pago.nota && <div><Text style={{ fontSize: 12 }}>{pago.nota}</Text></div>}
                            {pago.comprobante_path && (
                              <a href={storageUrl(pago.comprobante_path)} target="_blank" rel="noreferrer">
                                Ver comprobante
                              </a>
                            )}
                          </Col>
                          {canPagar && (
                            <Popconfirm
                              title="¿Eliminar este pago?"
                              description="Esta acción no se puede deshacer."
                              onConfirm={() => handleEliminarPago(detailOrden, pago)}
                              okText="Sí, eliminar"
                              cancelText="Cancelar"
                              okButtonProps={{ danger: true }}
                            >
                              <Button danger size="small" icon={<DeleteOutlined />} loading={eliminandoPagoId === pago.id} />
                            </Popconfirm>
                          )}
                        </Row>
                      </Card>
                    ))}
                  </Space>
                ) : (
                  <Empty description="Sin pagos registrados" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Modal: Checklist de Confirmación de Entrega ─────────────────────── */}
      <Modal
        title={`Confirmar Entrega — Orden ${entregaOrden?.numero_orden || ''}`}
        open={entregaModalOpen}
        onCancel={() => !entregaSaving && setEntregaModalOpen(false)}
        width={820}
        destroyOnClose
        maskClosable={!entregaSaving}
        footer={[
          <Button key="cancel" onClick={() => setEntregaModalOpen(false)} disabled={entregaSaving}>
            Cancelar
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<InboxOutlined />}
            loading={entregaSaving}
            onClick={handleSubmitEntrega}
          >
            Confirmar Entrega
          </Button>
        ]}
      >
        <Text type="secondary">
          Revisa cada producto recibido. Si todo llegó completo y en buen estado, deja "Bien" seleccionado.
          Si algo falla, cambia el estado, describe el problema y agrega fotos como evidencia.
        </Text>

        <Alert
          style={{ marginTop: 12, marginBottom: 16 }}
          showIcon
          type={entregaChecklist.some((it) => it.estado_recepcion !== 'ok') ? 'warning' : 'success'}
          message={
            entregaChecklist.some((it) => it.estado_recepcion !== 'ok')
              ? `${entregaChecklist.filter((it) => it.estado_recepcion !== 'ok').length} de ${entregaChecklist.length} producto(s) con incidencia reportada`
              : 'Todos los productos revisados sin incidencias'
          }
        />

        {entregaChecklist.map((item) => {
          const conProblema = item.estado_recepcion !== 'ok';
          return (
            <Card
              key={item.id}
              size="small"
              style={{ marginBottom: 12, borderColor: conProblema ? '#faad14' : undefined }}
            >
              <Row gutter={16} align="top">
                <Col xs={24} sm={8}>
                  <Text strong>{item.nombre}</Text>
                  <br />
                  <Text type="secondary">Pedido: {item.cantidad.toLocaleString('es-MX')} {item.unidad_medida}</Text>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ marginBottom: 4 }}><Text type="secondary">Cantidad recibida</Text></div>
                  <InputNumber
                    min={0}
                    precision={3}
                    style={{ width: '100%' }}
                    value={item.cantidad_recibida}
                    onChange={(v) => updateChecklistItem(item.id, { cantidad_recibida: v ?? 0 })}
                  />
                </Col>
                <Col xs={12} sm={10}>
                  <div style={{ marginBottom: 4 }}><Text type="secondary">Estado</Text></div>
                  <Segmented
                    block
                    value={item.estado_recepcion}
                    onChange={(v) => updateChecklistItem(item.id, { estado_recepcion: v })}
                    options={ESTADO_RECEPCION_OPTIONS}
                  />
                </Col>
              </Row>

              {conProblema && (
                <div style={{ marginTop: 12 }}>
                  <TextArea
                    rows={2}
                    placeholder="Describe el problema (obligatorio)"
                    value={item.nota_recepcion}
                    onChange={(e) => updateChecklistItem(item.id, { nota_recepcion: e.target.value })}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Upload
                      listType="picture-card"
                      fileList={item.evidencia_fotos.map((f) => ({
                        uid: f.uid, name: f.name, status: 'done', url: f.url
                      }))}
                      customRequest={(opts) => handleUploadEvidencia(item.id, opts)}
                      onRemove={(file) => handleRemoveEvidencia(item.id, file.uid)}
                      accept="image/*"
                    >
                      {item.evidencia_fotos.length < 4 && (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 4 }}>Foto</div>
                        </div>
                      )}
                    </Upload>
                  </div>
                </div>
              )}
            </Card>
          );
        })}

        <Divider plain>Notas generales de la entrega (opcional)</Divider>
        <TextArea
          rows={2}
          placeholder="Comentarios generales sobre la recepción..."
          value={entregaNotas}
          onChange={(e) => setEntregaNotas(e.target.value)}
        />
      </Modal>

      {/* ── Modal: Registrar Pago ────────────────────────────────────────── */}
      <Modal
        title={`Registrar Pago — Orden ${pagoOrden?.numero_orden || ''}`}
        open={pagoModalOpen}
        onCancel={() => !pagoSaving && setPagoModalOpen(false)}
        width={520}
        destroyOnClose
        maskClosable={!pagoSaving}
        footer={[
          <Button key="cancel" onClick={() => setPagoModalOpen(false)} disabled={pagoSaving}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" loading={pagoSaving} onClick={handleSubmitPago}>
            Registrar Pago
          </Button>
        ]}
      >
        {pagoOrden && (
          <>
            <Text type="secondary">
              Total de la orden: <Text strong>{formatCurrency(pagoOrden.total)}</Text>
              {' · '}Pagado hasta ahora: <Text strong>{formatCurrency(pagoOrden.monto_pagado || 0)}</Text>
              {' · '}Restante: <Text strong>{formatCurrency(Math.max(0, parseFloat(pagoOrden.total) - parseFloat(pagoOrden.monto_pagado || 0)))}</Text>
            </Text>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Monto</Text></div>
                <InputNumber
                  min={0.01}
                  precision={2}
                  prefix="$"
                  style={{ width: '100%' }}
                  value={pagoForm.monto}
                  onChange={(v) => updatePagoForm({ monto: v ?? 0 })}
                />
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Fecha de pago</Text></div>
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  value={pagoForm.fecha_pago}
                  onChange={(v) => updatePagoForm({ fecha_pago: v })}
                />
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Método de pago</Text></div>
                <Select
                  style={{ width: '100%' }}
                  value={pagoForm.metodo_pago}
                  onChange={(v) => updatePagoForm({ metodo_pago: v })}
                >
                  {METODOS_PAGO.map((m) => (
                    <Option key={m.value} value={m.value}>{m.label}</Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Referencia (opcional)</Text></div>
                <Input
                  placeholder="Folio, referencia bancaria..."
                  value={pagoForm.referencia}
                  onChange={(e) => updatePagoForm({ referencia: e.target.value })}
                />
              </Col>
            </Row>

            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 4 }}><Text type="secondary">Nota (opcional)</Text></div>
              <TextArea
                rows={2}
                placeholder="Comentarios sobre este pago..."
                value={pagoForm.nota}
                onChange={(e) => updatePagoForm({ nota: e.target.value })}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 4 }}><Text type="secondary">Comprobante (opcional)</Text></div>
              <Upload
                listType="picture-card"
                fileList={pagoForm.comprobante ? [{
                  uid: pagoForm.comprobante.uid,
                  name: pagoForm.comprobante.name,
                  status: 'done',
                  url: pagoForm.comprobante.url
                }] : []}
                customRequest={handleUploadComprobante}
                onRemove={() => updatePagoForm({ comprobante: null })}
                accept="image/*,.pdf"
                maxCount={1}
              >
                {!pagoForm.comprobante && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 4 }}>Subir</div>
                  </div>
                )}
              </Upload>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default OrdenesCompra;
